import React, { useState, useEffect, useMemo } from 'react';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer, importCustomers, getCustomerTemplate, checkCustomerDependencies, deleteCustomerCascade, getCustomerAddresses, addCustomerAddress, updateCustomerAddress, deleteCustomerAddress, getTransportOptions, openCustomersExport, getUseByProfiles, updateUseByProfile, relearnUseByProfile } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { canEdit } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { CountrySelect } from '../components/ui/country-select';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Upload, Download, Loader2, Users, AlertTriangle, FileText, Package, MapPin, Star, Building, ChevronLeft, ChevronRight, CalendarClock, RefreshCw } from 'lucide-react';

const emptyCustomer = {
  name: '',
  delivery_address_line1: '',
  delivery_address_line2: '',
  delivery_city: '',
  delivery_county: '',
  delivery_postcode: '',
  delivery_country: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  incident_contact_name: '',
  incident_contact_email: '',
  incident_contact_phone: '',
  incident_contacts: [],
  email_addresses: [],
  default_transport_agent: '',
  moq_alert_text: '',
  pallet_label_template: '',
  lidl_customer_code: '',
  print_dom_on_dispatch: false,
  auto_calc_weight: false,
  vat_number: '',
  accounts_reference: '',
  accountsiq_customer_code: '',
};

export default function Customers() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState(emptyCustomer);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [strictImport, setStrictImport] = useState(true); // Enable strict validation by default

  // Dependency check dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [dependencies, setDependencies] = useState(null);
  const [checkingDependencies, setCheckingDependencies] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Address management dialog
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [addresses, setAddresses] = useState({ primary_address: null, additional_addresses: [] });
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressFormOpen, setAddressFormOpen] = useState(false);
  const [addressFormData, setAddressFormData] = useState({
    label: '',
    alias_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    county: '',
    postcode: '',
    country: '',
    contact_name: '',
    contact_phone: '',
    delivery_instructions: '',
    is_default: false
  });
  const [savingAddress, setSavingAddress] = useState(false);

  // Use-By Shelf Life Profiles dialog
  const [shelfDialogOpen, setShelfDialogOpen] = useState(false);
  const [shelfCustomer, setShelfCustomer] = useState(null);
  const [shelfProfiles, setShelfProfiles] = useState([]);
  const [loadingShelf, setLoadingShelf] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState(null);
  const [editingDays, setEditingDays] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const loadShelfProfiles = async (customerId) => {
    setLoadingShelf(true);
    try {
      const { data } = await getUseByProfiles(customerId);
      setShelfProfiles(data.profiles || []);
    } catch {
      toast.error('Failed to load shelf life profiles');
    } finally {
      setLoadingShelf(false);
    }
  };

  const openShelfDialog = async (customer) => {
    setShelfCustomer(customer);
    setEditingProfileId(null);
    setShelfDialogOpen(true);
    await loadShelfProfiles(customer.id);
  };

  const handleSaveProfileDays = async (profile) => {
    const days = parseInt(editingDays);
    if (!days || days < 1 || days > 730) {
      toast.error('Enter shelf life days between 1 and 730');
      return;
    }
    setSavingProfile(true);
    try {
      await updateUseByProfile(profile.id, { shelf_life_days: days });
      toast.success(`Shelf life for ${profile.supplier_product_code} set to ${days} days (manual)`);
      setEditingProfileId(null);
      await loadShelfProfiles(shelfCustomer.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update shelf life');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleRelearnProfile = async (profile) => {
    try {
      const { data } = await relearnUseByProfile(profile.id);
      toast.success(data.message || 'Profile relearned from order history');
      await loadShelfProfiles(shelfCustomer.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to relearn profile');
    }
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Transport options sourced from Settings → Transport Options
  // (tenant-configured list, not a hardcoded enum)
  const [transportOptions, setTransportOptions] = useState([]);

  useEffect(() => {
    loadCustomers();
    loadTransportOptions();
  }, []);

  const loadTransportOptions = async () => {
    try {
      const res = await getTransportOptions();
      const list = Array.isArray(res.data) ? res.data : [];
      setTransportOptions(list);
    } catch (e) {
      // Non-fatal — dropdown will fall back to an empty list (only "None")
      console.error('Failed to load transport options', e);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await getCustomers({ search });
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Failed to load customers:', error.response?.status, error.message);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else if (!error.response) {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('Failed to load customers');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      loadCustomers();
    }, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Pagination calculations
  const totalPages = pageSize === 'all' ? 1 : Math.ceil(customers.length / pageSize);
  const paginatedCustomers = useMemo(() => {
    if (pageSize === 'all') return customers;
    const start = (currentPage - 1) * pageSize;
    return customers.slice(start, start + pageSize);
  }, [customers, currentPage, pageSize]);

  const openCreateDialog = () => {
    setEditingCustomer(null);
    setFormData(emptyCustomer);
    setDialogOpen(true);
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || '',
      delivery_address_line1: customer.delivery_address_line1 || '',
      delivery_address_line2: customer.delivery_address_line2 || '',
      delivery_city: customer.delivery_city || '',
      delivery_county: customer.delivery_county || '',
      delivery_postcode: customer.delivery_postcode || '',
      delivery_country: customer.delivery_country || 'Ireland',
      contact_name: customer.contact_name || '',
      contact_email: customer.contact_email || '',
      contact_phone: customer.contact_phone || '',
      incident_contact_name: customer.incident_contact_name || '',
      incident_contact_email: customer.incident_contact_email || '',
      incident_contact_phone: customer.incident_contact_phone || '',
      incident_contacts: customer.incident_contacts || [],
      email_addresses: customer.email_addresses || [],
      default_transport_agent: customer.default_transport_agent || '',
      moq_alert_text: customer.moq_alert_text || '',
      pallet_label_template: customer.pallet_label_template || '',
      lidl_customer_code: customer.lidl_customer_code || '',
      print_dom_on_dispatch: customer.print_dom_on_dispatch || false,
      auto_calc_weight: customer.auto_calc_weight || false,
      vat_number: customer.vat_number || '',
      accounts_reference: customer.accounts_reference || '',
      accountsiq_customer_code: customer.accountsiq_customer_code || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.delivery_address_line1) {
      toast.error('Name and delivery address are required');
      return;
    }

    setSaving(true);
    try {
      if (editingCustomer) {
        await updateCustomer(editingCustomer.id, formData);
        toast.success('Customer updated');
      } else {
        await createCustomer(formData);
        toast.success('Customer created');
      }
      setDialogOpen(false);
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customer) => {
    // First check dependencies
    setCustomerToDelete(customer);
    setCheckingDependencies(true);
    setDeleteDialogOpen(true);
    
    try {
      const response = await checkCustomerDependencies(customer.id);
      setDependencies(response.data);
    } catch (error) {
      toast.error('Failed to check dependencies');
      setDeleteDialogOpen(false);
    } finally {
      setCheckingDependencies(false);
    }
  };

  const confirmDelete = async () => {
    if (!customerToDelete) return;
    
    setDeleting(true);
    try {
      if (dependencies?.future_count > 0) {
        // Use cascade delete
        await deleteCustomerCascade(customerToDelete.id, {
          delete_future_dispatches: true,
          confirm: true
        });
        toast.success(`Customer and ${dependencies.future_count} future dispatch(es) deleted`);
      } else {
        // Simple delete
        await deleteCustomer(customerToDelete.id);
        toast.success('Customer deactivated');
      }
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
      setDependencies(null);
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    } finally {
      setDeleting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const response = await importCustomers(file, strictImport);
      const { imported, updated, skipped, errors, rejected, validation_errors } = response.data;
      
      // Build result message
      let msg = `Imported ${imported || 0}, updated ${updated || 0}, skipped ${skipped || 0}`;
      if (rejected > 0) {
        msg += `, rejected ${rejected} (incomplete data)`;
      }
      if (errors?.length > 0) {
        msg += `. Errors: ${errors.length}`;
      }
      
      if (rejected > 0 || errors?.length > 0) {
        toast.warning(msg, { duration: 6000 });
        
        // Log detailed validation errors
        if (validation_errors?.length > 0) {
          console.warn('Validation errors (rejected rows):', validation_errors);
          toast.error(
            <div>
              <p className="font-semibold mb-1">Rejected rows (missing required fields):</p>
              <ul className="text-xs list-disc list-inside max-h-32 overflow-y-auto">
                {validation_errors.slice(0, 5).map((err, i) => (
                  <li key={i}>Row {err.row}: {err.customer_name} - {err.missing_fields.join(', ')}</li>
                ))}
                {validation_errors.length > 5 && <li>...and {validation_errors.length - 5} more</li>}
              </ul>
            </div>,
            { duration: 10000 }
          );
        }
      } else {
        toast.success(msg);
      }
      
      if (errors?.length > 0) {
        console.error('Import errors:', errors);
      }
      setImportDialogOpen(false);
      loadCustomers();
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(getCustomerTemplate(), '_blank');
  };

  const handleExportCustomers = async () => {
    // SEC-001: routed through openSecureDownload → single-use download token.
    await openCustomersExport();
  };

  // Address Management Functions
  const openAddressDialog = async (customer) => {
    setSelectedCustomer(customer);
    setAddressDialogOpen(true);
    setLoadingAddresses(true);
    try {
      const response = await getCustomerAddresses(customer.id);
      setAddresses(response.data);
    } catch (error) {
      toast.error('Failed to load addresses');
    } finally {
      setLoadingAddresses(false);
    }
  };

  const openAddressForm = (address = null) => {
    if (address) {
      setEditingAddress(address);
      setAddressFormData({
        label: address.label || '',
        alias_name: address.alias_name || '',
        address_line1: address.address_line1 || '',
        address_line2: address.address_line2 || '',
        city: address.city || '',
        county: address.county || '',
        postcode: address.postcode || '',
        country: address.country || 'Ireland',
        contact_name: address.contact_name || '',
        contact_phone: address.contact_phone || '',
        delivery_instructions: address.delivery_instructions || '',
        is_default: address.is_default || false
      });
    } else {
      setEditingAddress(null);
      setAddressFormData({
        label: '',
        alias_name: '',
        address_line1: '',
        address_line2: '',
        city: '',
        county: '',
        postcode: '',
        country: '',
        contact_name: '',
        contact_phone: '',
        delivery_instructions: '',
        is_default: false
      });
    }
    setAddressFormOpen(true);
  };

  const handleSaveAddress = async () => {
    if (!addressFormData.label || !addressFormData.address_line1) {
      toast.error('Label and address line 1 are required');
      return;
    }

    setSavingAddress(true);
    try {
      if (editingAddress && editingAddress.id !== 'primary') {
        await updateCustomerAddress(selectedCustomer.id, editingAddress.id, addressFormData);
        toast.success('Address updated');
      } else {
        await addCustomerAddress(selectedCustomer.id, addressFormData);
        toast.success('Address added');
      }
      
      // Reload addresses
      const response = await getCustomerAddresses(selectedCustomer.id);
      setAddresses(response.data);
      setAddressFormOpen(false);
      // Refresh the customer list so the address-count badge stays in sync
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save address');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address? This cannot be undone.')) return;

    try {
      await deleteCustomerAddress(selectedCustomer.id, addressId);
      toast.success('Address deleted');
      // Reload addresses inside the dialog
      const response = await getCustomerAddresses(selectedCustomer.id);
      setAddresses(response.data);
      // Keep customer-list badge in sync
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete address');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-1 w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search customers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-customers"
          />
        </div>
        {canEdit(user?.role) && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={handleExportCustomers} size="sm" className="sm:h-10" data-testid="btn-export">
              <Download className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Export</span>
            </Button>
            <Button variant="outline" onClick={() => setImportDialogOpen(true)} size="sm" className="sm:h-10" data-testid="btn-import">
              <Upload className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Import </span>Excel
            </Button>
            <Button onClick={openCreateDialog} size="sm" className="sm:h-10" data-testid="btn-add-customer">
              <Plus className="w-4 h-4 mr-1 sm:mr-2" />
              Add Customer
            </Button>
          </div>
        )}
      </div>

      {/* Results count and pagination controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-sm text-muted-foreground">
        <span>
          {customers.length} customer{customers.length !== 1 ? 's' : ''} found
          {pageSize !== 'all' && totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs">Show:</span>
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(v === 'all' ? 'all' : parseInt(v)); setCurrentPage(1); }}>
            <SelectTrigger className="w-20 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          {pageSize !== 'all' && totalPages > 1 && (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium">{currentPage}</span>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Customers Table */}
      {paginatedCustomers.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Name</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Address</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Contact</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Phone</th>
                    <th className="w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paginatedCustomers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-muted/30" data-testid={`customer-row-${customer.id}`}>
                      <td className="px-4 py-3 font-medium">{customer.name}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <span>{[customer.delivery_address_line1, customer.delivery_city].filter(Boolean).join(', ')}</span>
                          {(customer.additional_addresses?.length > 0) && (
                            <span className="text-xs bg-muted px-1.5 py-0.5 rounded">
                              +{customer.additional_addresses.length}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{customer.contact_name || '-'}</td>
                      <td className="px-4 py-3 text-sm font-mono">{customer.contact_phone || '-'}</td>
                      <td className="px-4 py-3">
                        {canEdit(user?.role) && (
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => openAddressDialog(customer)} title="Manage Addresses">
                              <MapPin className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openShelfDialog(customer)} title="Use-By Shelf Life Profiles" data-testid={`btn-shelf-life-${customer.id}`}>
                              <CalendarClock className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(customer)} data-testid={`btn-edit-customer-${customer.id}`} title="Edit customer">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(customer)} data-testid={`btn-delete-customer-${customer.id}`} title="Delete customer">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">No customers found</h3>
            <p className="text-muted-foreground mb-4">
              {search ? 'Try adjusting your search' : 'Get started by adding your first customer'}
            </p>
            {!search && canEdit(user?.role) && (
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add Customer
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Customer Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="input-customer-name"
              />
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-4">Delivery Address</p>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label>Address Line 1 *</Label>
                  <Input
                    value={formData.delivery_address_line1}
                    onChange={(e) => setFormData({ ...formData, delivery_address_line1: e.target.value })}
                    data-testid="input-address-line1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address Line 2</Label>
                  <Input
                    value={formData.delivery_address_line2}
                    onChange={(e) => setFormData({ ...formData, delivery_address_line2: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.delivery_city}
                      onChange={(e) => setFormData({ ...formData, delivery_city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>County</Label>
                    <Input
                      value={formData.delivery_county}
                      onChange={(e) => setFormData({ ...formData, delivery_county: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Postcode</Label>
                    <Input
                      value={formData.delivery_postcode}
                      onChange={(e) => setFormData({ ...formData, delivery_postcode: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <CountrySelect
                      value={formData.delivery_country || ''}
                      onValueChange={(value) => setFormData({ ...formData, delivery_country: value })}
                      data-testid="country-select"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-4">Contact Details</p>
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contact Name</Label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Phone</Label>
                    <Input
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  />
                </div>
                {/* Additional Email Addresses */}
                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span>Additional Email Addresses</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={() => setFormData({ ...formData, email_addresses: [...(formData.email_addresses || []), ''] })}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </Label>
                  {(formData.email_addresses || []).map((email, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        type="email"
                        value={email}
                        placeholder="email@example.com"
                        onChange={(e) => {
                          const updated = [...(formData.email_addresses || [])];
                          updated[i] = e.target.value;
                          setFormData({ ...formData, email_addresses: updated });
                        }}
                        data-testid={`input-extra-email-${i}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-9 px-2 text-red-500 hover:text-red-700"
                        onClick={() => {
                          const updated = (formData.email_addresses || []).filter((_, idx) => idx !== i);
                          setFormData({ ...formData, email_addresses: updated });
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Incident / Withdrawal / Recall Management Contacts ──
                Dynamic list of contacts (QA Manager, Director, etc). Each
                contact has a name plus 1+ emails and 1+ phones. All emails
                are auto-added to recall recipient previews. */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-medium">Incident / Withdrawal / Recall Contacts</p>
                  <p className="text-xs text-muted-foreground">
                    Contacts notified for FSAI / BRC recall workflows (typically a QA Manager and a Director). Each contact can have multiple emails &amp; phones (fixed line + mobile). All emails get included in the recall preview — operator can edit before sending.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFormData({
                    ...formData,
                    incident_contacts: [
                      ...(formData.incident_contacts || []),
                      {
                        name: formData.contact_name || '',
                        emails: formData.contact_email ? [formData.contact_email] : [],
                        phones: formData.contact_phone ? [formData.contact_phone] : [],
                      },
                    ],
                  })}
                  data-testid="btn-copy-incident-from-contact"
                  disabled={!formData.contact_name && !formData.contact_email && !formData.contact_phone}
                  title="Add a new incident contact pre-filled with the values from the Contact Details section above"
                >
                  Copy from above
                </Button>
              </div>

              <div className="space-y-3" data-testid="incident-contacts-list">
                {(formData.incident_contacts || []).map((contact, idx) => (
                  <div key={idx} className="rounded-md border bg-zinc-50/50 p-3 space-y-3" data-testid={`incident-contact-card-${idx}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1 space-y-2">
                        <Label className="text-xs uppercase tracking-wide text-zinc-500">
                          Contact #{idx + 1} Name
                        </Label>
                        <Input
                          value={contact.name || ''}
                          onChange={(e) => {
                            const next = [...(formData.incident_contacts || [])];
                            next[idx] = { ...next[idx], name: e.target.value };
                            setFormData({ ...formData, incident_contacts: next });
                          }}
                          placeholder={idx === 0 ? 'e.g. QA / Technical Manager' : 'e.g. Operations Director'}
                          data-testid={`input-incident-contact-name-${idx}`}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-9 mt-6 text-red-500 hover:text-red-700"
                        onClick={() => {
                          const next = (formData.incident_contacts || []).filter((_, i) => i !== idx);
                          setFormData({ ...formData, incident_contacts: next });
                        }}
                        data-testid={`btn-remove-incident-contact-${idx}`}
                        title="Remove this incident contact"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>

                    {/* Emails */}
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wide text-zinc-500">Emails</Label>
                      {(contact.emails || ['']).map((email, ei) => (
                        <div key={ei} className="flex items-center gap-2">
                          <Input
                            type="email"
                            value={email}
                            onChange={(e) => {
                              const next = [...(formData.incident_contacts || [])];
                              const emails = [...(next[idx].emails || [])];
                              emails[ei] = e.target.value;
                              next[idx] = { ...next[idx], emails };
                              setFormData({ ...formData, incident_contacts: next });
                            }}
                            placeholder="recall@customer.example"
                            className="h-9"
                            data-testid={`input-incident-contact-email-${idx}-${ei}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-9 px-2 text-red-500 hover:text-red-700"
                            onClick={() => {
                              const next = [...(formData.incident_contacts || [])];
                              next[idx] = { ...next[idx], emails: (next[idx].emails || []).filter((_, i) => i !== ei) };
                              setFormData({ ...formData, incident_contacts: next });
                            }}
                            title="Remove email"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          const next = [...(formData.incident_contacts || [])];
                          next[idx] = { ...next[idx], emails: [...(next[idx].emails || []), ''] };
                          setFormData({ ...formData, incident_contacts: next });
                        }}
                        data-testid={`btn-add-incident-email-${idx}`}
                      >
                        + Add another email
                      </Button>
                    </div>

                    {/* Phones */}
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wide text-zinc-500">Phones (fixed line, mobile, etc.)</Label>
                      {(contact.phones || ['']).map((phone, pi) => (
                        <div key={pi} className="flex items-center gap-2">
                          <Input
                            value={phone}
                            onChange={(e) => {
                              const next = [...(formData.incident_contacts || [])];
                              const phones = [...(next[idx].phones || [])];
                              phones[pi] = e.target.value;
                              next[idx] = { ...next[idx], phones };
                              setFormData({ ...formData, incident_contacts: next });
                            }}
                            placeholder={pi === 0 ? '+353 1 555 1234 (fixed line)' : '+353 87 555 5678 (mobile)'}
                            className="h-9"
                            data-testid={`input-incident-contact-phone-${idx}-${pi}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-9 px-2 text-red-500 hover:text-red-700"
                            onClick={() => {
                              const next = [...(formData.incident_contacts || [])];
                              next[idx] = { ...next[idx], phones: (next[idx].phones || []).filter((_, i) => i !== pi) };
                              setFormData({ ...formData, incident_contacts: next });
                            }}
                            title="Remove phone"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          const next = [...(formData.incident_contacts || [])];
                          next[idx] = { ...next[idx], phones: [...(next[idx].phones || []), ''] };
                          setFormData({ ...formData, incident_contacts: next });
                        }}
                        data-testid={`btn-add-incident-phone-${idx}`}
                      >
                        + Add another phone
                      </Button>
                    </div>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData({
                    ...formData,
                    incident_contacts: [
                      ...(formData.incident_contacts || []),
                      { name: '', emails: [''], phones: [''] },
                    ],
                  })}
                  data-testid="btn-add-incident-contact"
                >
                  + Add Incident Contact
                </Button>
              </div>
            </div>

            {/* Transport & Order Settings */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-4">Transport & Order Settings</p>
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Default Transport Agent</Label>
                    <Select
                      value={formData.default_transport_agent || ''}
                      onValueChange={(value) => setFormData({ ...formData, default_transport_agent: value })}
                    >
                      <SelectTrigger data-testid="select-default-transport">
                        <SelectValue placeholder="None" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none_selected">None</SelectItem>
                        {transportOptions.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                        {/* If the saved value is no longer in the configured
                            list, keep it visible so the user doesn't lose it
                            silently — they can re-pick when they update
                            the customer. */}
                        {formData.default_transport_agent
                          && formData.default_transport_agent !== 'none_selected'
                          && !transportOptions.includes(formData.default_transport_agent) && (
                          <SelectItem
                            key={formData.default_transport_agent}
                            value={formData.default_transport_agent}
                          >
                            {formData.default_transport_agent} (legacy)
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Pallet Label Template</Label>
                    <Select
                      value={formData.pallet_label_template || ''}
                      onValueChange={(value) => setFormData({ ...formData, pallet_label_template: value === 'auto' ? '' : value })}
                    >
                      <SelectTrigger data-testid="select-pallet-template">
                        <SelectValue placeholder="Auto-detect" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto-detect (by name)</SelectItem>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="agropur">Agropur</SelectItem>
                        <SelectItem value="aldi">Aldi</SelectItem>
                        <SelectItem value="lidl">Lidl (Shipment Note)</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.pallet_label_template === 'lidl' && (
                      <div className="mt-2 space-y-1">
                        <Label className="text-xs">Lidl Customer No.</Label>
                        <Input
                          value={formData.lidl_customer_code || ''}
                          onChange={(e) => setFormData({ ...formData, lidl_customer_code: e.target.value })}
                          placeholder="e.g. LID006"
                          maxLength={16}
                          data-testid="input-lidl-customer-code"
                        />
                        <p className="text-[11px] text-muted-foreground">
                          Prints in the &quot;Customer No.&quot; field on the Lidl shipment-note pallet label.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Minimum Order Alert</Label>
                  <Textarea
                    value={formData.moq_alert_text || ''}
                    onChange={(e) => setFormData({ ...formData, moq_alert_text: e.target.value })}
                    placeholder="e.g. Min 50 cases across all products"
                    rows={2}
                    className="text-sm"
                    data-testid="input-moq-alert"
                  />
                </div>
                <div className="flex items-start gap-2 p-3 rounded-md border bg-muted/30">
                  <input
                    type="checkbox"
                    id="print_dom_on_dispatch"
                    checked={!!formData.print_dom_on_dispatch}
                    onChange={(e) => setFormData({ ...formData, print_dom_on_dispatch: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-purple-600 focus:ring-purple-500"
                    data-testid="toggle-print-dom"
                  />
                  <label htmlFor="print_dom_on_dispatch" className="cursor-pointer">
                    <span className="text-sm font-medium">Show Date of Manufacture (D.O.M.) on dispatch note</span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      When enabled, a per-batch D.O.M. input appears in the batch entry dialog for this customer's orders, and a D.O.M. column is added to the dispatch note PDF.
                    </p>
                  </label>
                </div>
                <div className="flex items-start gap-2 p-3 rounded-md border bg-muted/30">
                  <input
                    type="checkbox"
                    id="auto_calc_weight"
                    checked={!!formData.auto_calc_weight}
                    onChange={(e) => setFormData({ ...formData, auto_calc_weight: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
                    data-testid="toggle-auto-calc-weight"
                  />
                  <label htmlFor="auto_calc_weight" className="cursor-pointer">
                    <span className="text-sm font-medium">Auto-calculate weight (FIXED WEIGHT products)</span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      When enabled, batch weight is auto-filled from <em>Product → weight per unit</em> as soon as units are entered. Leave OFF for variable-weight products where weight must be manually entered.
                    </p>
                  </label>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-4">Other Details</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>VAT Number</Label>
                  <Input
                    value={formData.vat_number}
                    onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Accounts Reference</Label>
                  <Input
                    value={formData.accounts_reference}
                    onChange={(e) => setFormData({ ...formData, accounts_reference: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>AccountsIQ Customer Code</Label>
                  <Input
                    value={formData.accountsiq_customer_code}
                    onChange={(e) => setFormData({ ...formData, accountsiq_customer_code: e.target.value })}
                    placeholder="e.g. GOUR01"
                    data-testid="input-accountsiq-customer-code"
                  />
                  <p className="text-xs text-muted-foreground">
                    Needed to post event invoices to AccountsIQ. Customers pulled from the AccountsIQ sync already have one.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-customer">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingCustomer ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Customers from Excel</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Upload an Excel file with customer data. The system will attempt to map columns automatically.
              Expected columns: Customer/Name, Address, Email, Phone, PO Number
            </p>
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="mb-4" data-testid="btn-download-template">
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            
            {/* Strict validation option */}
            <div className="flex items-center space-x-2 mb-4 p-3 bg-muted/50 rounded-lg">
              <Checkbox
                id="strict-import"
                checked={strictImport}
                onCheckedChange={(checked) => setStrictImport(checked)}
              />
              <div>
                <label htmlFor="strict-import" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Strict Validation
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  Reject rows missing required fields (name + address). Recommended for clean data imports.
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Upload Excel File</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImport}
                disabled={importing}
                data-testid="input-import-file"
              />
            </div>
            {importing && (
              <div className="flex items-center gap-2 mt-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Importing...</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog with Dependencies */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setDeleteDialogOpen(false);
          setCustomerToDelete(null);
          setDependencies(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Customer
            </DialogTitle>
            <DialogDescription>
              {customerToDelete?.name}
            </DialogDescription>
          </DialogHeader>
          
          {checkingDependencies ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Checking dependencies...</span>
            </div>
          ) : dependencies ? (
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">{dependencies.message}</p>
              
              {dependencies.planned_count > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
                    <FileText className="w-4 h-4" />
                    {dependencies.planned_count} Planned Order(s) from Weekly Planner - Will be deleted
                  </div>
                  <div className="max-h-32 overflow-y-auto text-sm text-amber-600 space-y-1">
                    {dependencies.planned_orders?.slice(0, 5).map(d => (
                      <div key={d.id} className="font-mono">
                        {d.dispatch_number} ({d.status}) - {d.dispatch_date}
                      </div>
                    ))}
                    {dependencies.planned_count > 5 && (
                      <div className="text-xs">...and {dependencies.planned_count - 5} more</div>
                    )}
                  </div>
                </div>
              )}
              
              {dependencies.ready_count > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-orange-700 font-medium mb-2">
                    <Package className="w-4 h-4" />
                    {dependencies.ready_count} Ready Order(s) - Will be deleted
                  </div>
                  <div className="max-h-32 overflow-y-auto text-sm text-orange-600 space-y-1">
                    {dependencies.ready_orders?.slice(0, 5).map(d => (
                      <div key={d.id} className="font-mono">
                        {d.dispatch_number} (locked) - {d.dispatch_date}
                      </div>
                    ))}
                    {dependencies.ready_count > 5 && (
                      <div className="text-xs">...and {dependencies.ready_count - 5} more</div>
                    )}
                  </div>
                </div>
              )}
              
              {dependencies.past_count > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-blue-700 font-medium mb-2">
                    <Package className="w-4 h-4" />
                    {dependencies.past_count} Past Dispatch(es) - Will be preserved
                  </div>
                  <p className="text-sm text-blue-600">
                    These dispatches have already been completed. Customer name will be retained in records.
                  </p>
                </div>
              )}
              
              {!dependencies.has_dependencies && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700">
                  No dependencies found. Safe to delete.
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              disabled={checkingDependencies || deleting}
            >
              {deleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
              ) : dependencies?.future_count > 0 ? (
                <><Trash2 className="w-4 h-4 mr-2" /> Delete Customer & {dependencies.future_count} Order(s)</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Delete Customer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Address Management Dialog */}
      <Dialog open={addressDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setAddressDialogOpen(false);
          setSelectedCustomer(null);
          setAddresses({ primary_address: null, additional_addresses: [] });
          setAddressFormOpen(false);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Delivery Addresses
            </DialogTitle>
            <DialogDescription>
              {selectedCustomer?.name} - Manage delivery locations
            </DialogDescription>
          </DialogHeader>
          
          {loadingAddresses ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Loading addresses...</span>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* Primary Address */}
              {addresses.primary_address && (
                <div className="border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 mb-2">
                      <Building className="w-4 h-4 text-primary" />
                      <span className="font-medium">{addresses.primary_address.label}</span>
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Primary</span>
                      {addresses.primary_address.is_default && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded flex items-center gap-1">
                          <Star className="w-3 h-3" /> Default
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-0.5">
                    <p>{addresses.primary_address.address_line1}</p>
                    {addresses.primary_address.address_line2 && <p>{addresses.primary_address.address_line2}</p>}
                    <p>{[addresses.primary_address.city, addresses.primary_address.county, addresses.primary_address.postcode].filter(Boolean).join(', ')}</p>
                    <p>{addresses.primary_address.country}</p>
                    {addresses.primary_address.contact_name && (
                      <p className="pt-1 text-foreground">Contact: {addresses.primary_address.contact_name} {addresses.primary_address.contact_phone && `(${addresses.primary_address.contact_phone})`}</p>
                    )}
                    {addresses.primary_address.delivery_instructions && (
                      <p className="pt-1 italic">&ldquo;{addresses.primary_address.delivery_instructions}&rdquo;</p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Primary address is managed via customer details. Edit the customer to change.
                  </p>
                </div>
              )}

              {/* Additional Addresses */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">
                    Additional Addresses
                    {addresses.additional_addresses?.length > 0 && (
                      <span className="ml-2 text-xs text-muted-foreground font-normal">
                        ({addresses.additional_addresses.length})
                      </span>
                    )}
                  </h4>
                  <Button size="sm" onClick={() => openAddressForm()} data-testid="btn-add-address">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Address
                  </Button>
                </div>

                {addresses.additional_addresses?.length > 0 ? (
                  addresses.additional_addresses.map((addr) => (
                    <div key={addr.id} className="border rounded-lg p-4" data-testid={`additional-address-${addr.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{addr.label}</span>
                          {addr.is_default && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded flex items-center gap-1">
                              <Star className="w-3 h-3" /> Default
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openAddressForm(addr)} data-testid={`btn-edit-address-${addr.id}`}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteAddress(addr.id)} data-testid={`btn-delete-address-${addr.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground space-y-0.5">
                        <p>{addr.address_line1}</p>
                        {addr.address_line2 && <p>{addr.address_line2}</p>}
                        <p>{[addr.city, addr.county, addr.postcode].filter(Boolean).join(', ')}</p>
                        {addr.contact_name && (
                          <p className="pt-1 text-foreground">Contact: {addr.contact_name} {addr.contact_phone && `(${addr.contact_phone})`}</p>
                        )}
                        {addr.delivery_instructions && (
                          <p className="pt-1 italic">&ldquo;{addr.delivery_instructions}&rdquo;</p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6 text-muted-foreground border rounded-lg border-dashed" data-testid="no-additional-addresses">
                    <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No additional addresses</p>
                    <p className="text-xs">Add delivery locations for this customer</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddressDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Address Form Dialog */}
      <Dialog open={addressFormOpen} onOpenChange={setAddressFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAddress ? 'Edit Address' : 'Add Delivery Address'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Label / Name *</Label>
              <Input
                value={addressFormData.label}
                onChange={(e) => setAddressFormData({ ...addressFormData, label: e.target.value })}
                placeholder="e.g., Dublin Warehouse, Cork Branch"
                data-testid="input-address-label"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                <span>Alias Name (printed on dispatch note)</span>
                <span className="text-xs text-zinc-500">{(addressFormData.alias_name || '').length}/20</span>
              </Label>
              <Input
                value={addressFormData.alias_name}
                maxLength={20}
                onChange={(e) => setAddressFormData({ ...addressFormData, alias_name: e.target.value.slice(0, 20) })}
                placeholder="e.g., Aldi Mitchelstown DC"
                data-testid="input-address-alias-name"
              />
              <p className="text-xs text-zinc-500">
                Optional. If set, this will print in bold under the customer name on the
                Standard Dispatch Note and Pallet Label when this address is selected on a sales order.
                Max 20 characters.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Address Line 1 *</Label>
              <Input
                value={addressFormData.address_line1}
                onChange={(e) => setAddressFormData({ ...addressFormData, address_line1: e.target.value })}
                data-testid="input-address-line1"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Address Line 2</Label>
              <Input
                value={addressFormData.address_line2}
                onChange={(e) => setAddressFormData({ ...addressFormData, address_line2: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={addressFormData.city}
                  onChange={(e) => setAddressFormData({ ...addressFormData, city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>County</Label>
                <Input
                  value={addressFormData.county}
                  onChange={(e) => setAddressFormData({ ...addressFormData, county: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Postcode</Label>
                <Input
                  value={addressFormData.postcode}
                  onChange={(e) => setAddressFormData({ ...addressFormData, postcode: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <CountrySelect
                  value={addressFormData.country || ''}
                  onValueChange={(value) => setAddressFormData({ ...addressFormData, country: value })}
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-4">Contact at this Location</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name</Label>
                  <Input
                    value={addressFormData.contact_name}
                    onChange={(e) => setAddressFormData({ ...addressFormData, contact_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input
                    value={addressFormData.contact_phone}
                    onChange={(e) => setAddressFormData({ ...addressFormData, contact_phone: e.target.value })}
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Delivery Instructions</Label>
              <Textarea
                value={addressFormData.delivery_instructions}
                onChange={(e) => setAddressFormData({ ...addressFormData, delivery_instructions: e.target.value })}
                placeholder="Special delivery notes..."
                rows={2}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_default"
                checked={addressFormData.is_default}
                onCheckedChange={(checked) => setAddressFormData({ ...addressFormData, is_default: checked })}
              />
              <label htmlFor="is_default" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Set as default delivery address
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddressFormOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAddress} disabled={savingAddress} data-testid="btn-save-address">
              {savingAddress ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingAddress ? 'Update' : 'Add'} Address
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Use-By Shelf Life Profiles Dialog */}
      <Dialog open={shelfDialogOpen} onOpenChange={setShelfDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-violet-600" />
              Use-By Shelf Life — {shelfCustomer?.name}
            </DialogTitle>
            <DialogDescription>
              Shelf-life days learned per <strong>Customer Product Code</strong> from this customer's past orders.
              These drive the Use-By date auto-suggest during batch entry. Manual overrides are never changed by auto-learning.
            </DialogDescription>
          </DialogHeader>

          {loadingShelf ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : shelfProfiles.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground" data-testid="shelf-profiles-empty">
              <CalendarClock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm font-medium">No use-by history learned yet for this customer.</p>
              <p className="text-xs mt-1">Run "Scan Past Dispatches for Use-By Patterns" in Data Management,<br />or profiles will build automatically as new orders are processed.</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden" data-testid="shelf-profiles-table">
              <table className="w-full text-sm">
                <thead className="bg-violet-50">
                  <tr>
                    <th className="text-left p-2.5 font-medium text-violet-900">Customer Code</th>
                    <th className="text-left p-2.5 font-medium text-violet-900">Product</th>
                    <th className="text-right p-2.5 font-medium text-violet-900">Shelf Life</th>
                    <th className="text-right p-2.5 font-medium text-violet-900">Consistency</th>
                    <th className="text-left p-2.5 font-medium text-violet-900">Source</th>
                    <th className="text-right p-2.5 font-medium text-violet-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {shelfProfiles.map((p) => {
                    const consistency = p.sample_count > 0 ? (p.mode_count || 0) / p.sample_count : 0;
                    return (
                      <tr key={p.id} className="hover:bg-muted/30" data-testid={`shelf-profile-row-${p.supplier_product_code}`}>
                        <td className="p-2.5 font-mono text-xs font-semibold">{p.supplier_product_code}</td>
                        <td className="p-2.5 text-xs">{p.product_name || '—'}</td>
                        <td className="p-2.5 text-right">
                          {editingProfileId === p.id ? (
                            <div className="flex items-center gap-1.5 justify-end">
                              <Input
                                type="number"
                                min="1"
                                max="730"
                                value={editingDays}
                                onChange={(e) => setEditingDays(e.target.value)}
                                className="w-20 h-8 font-mono text-sm text-right"
                                data-testid={`input-shelf-days-${p.supplier_product_code}`}
                                autoFocus
                              />
                              <Button
                                size="sm"
                                className="h-8 px-2 bg-violet-600 hover:bg-violet-700"
                                disabled={savingProfile}
                                onClick={() => handleSaveProfileDays(p)}
                                data-testid={`btn-save-shelf-days-${p.supplier_product_code}`}
                              >
                                {savingProfile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                              </Button>
                              <Button size="sm" variant="ghost" className="h-8 px-1.5" onClick={() => setEditingProfileId(null)}>✕</Button>
                            </div>
                          ) : (
                            <span className="font-mono font-bold text-base">
                              {p.shelf_life_days}
                              <span className="text-xs font-normal text-muted-foreground ml-1">days</span>
                            </span>
                          )}
                        </td>
                        <td className="p-2.5 text-right text-xs">
                          {p.sample_count > 0 ? (
                            <span
                              className={consistency < 0.8 ? 'text-amber-600 font-medium' : 'text-muted-foreground'}
                              title={consistency < 0.8 ? 'Inconsistent history — worth checking with this customer' : 'Orders matching the learned shelf life'}
                            >
                              {p.mode_count}/{p.sample_count} orders
                            </span>
                          ) : '—'}
                        </td>
                        <td className="p-2.5">
                          {p.source === 'manual' ? (
                            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold">MANUAL</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold">LEARNED</span>
                          )}
                        </td>
                        <td className="p-2.5">
                          {canEdit(user?.role) && (
                            <div className="flex gap-1 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="Manually set shelf life days"
                                onClick={() => { setEditingProfileId(p.id); setEditingDays(p.shelf_life_days?.toString() || ''); }}
                                data-testid={`btn-edit-shelf-${p.supplier_product_code}`}
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="Relearn from order history (clears manual override)"
                                onClick={() => handleRelearnProfile(p)}
                                data-testid={`btn-relearn-shelf-${p.supplier_product_code}`}
                              >
                                <RefreshCw className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShelfDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
